import * as qs from "qs"
import {
  doit,
  isNone,
  generateUUID,
  isPlainObject,
  reflect,
  asArray,
} from "./functions"
import {
  selectElement,
  dispatchElement,
  valueOfXNode
} from "./domHelper"

/*
  urlTransform(url, {
    deleteParams: ["staff_payroll"], // staff_payroll 혹은 staff_payroll[ 로 시작하는 모든 파라미터를 삭제 
    assignParams: {
      staff_payroll_id: id,
      staff_payroll_type: "StaffPayroll",
    }, // staff_payroll_id, staff_payroll_type 파라미터를 추가
    appendParams: {
    } // 파라메터 엔트리 추가
  })
*/
export function urlTransform(url, transformParams = {}) {
  // 상대 경로 처리: turbo-frame src에서 오는 상대 경로 지원
  let originalUrl;
  try {
    // 절대 URL인 경우
    originalUrl = new URL(url);
  } catch (e) {
    // 상대 경로인 경우 현재 origin과 합쳐서 URL 객체 생성
    originalUrl = new URL(url, window.location.origin);
  }

  // transformParams 유효성 검증
  const validKeys = ['deleteParams', 'assignParams', 'appendParams'];
  const providedKeys = Object.keys(transformParams);

  if (providedKeys.length === 0) {
    throw new Error('urlTransform(): transformParams가 비어있습니다. deleteParams, assignParams, appendParams 중 하나 이상을 제공해야 합니다.');
  }

  const invalidKeys = providedKeys.filter(key => !validKeys.includes(key));
  if (invalidKeys.length > 0) {
    throw new Error(`urlTransform(): 유효하지 않은 키들: ${invalidKeys.join(', ')}. 허용된 키: ${validKeys.join(', ')}`);
  }

  const { deleteParams, assignParams, appendParams } = transformParams;

  // deleteParams 검증: 배열만 허용
  if (deleteParams !== undefined) {
    if (!Array.isArray(deleteParams)) {
      throw new Error('urlTransform(): deleteParams는 배열이어야 합니다.');
    }
  }

  // assignParams 검증: 객체 또는 객체 배열 허용
  if (assignParams !== undefined) {
    if (!isPlainObject(assignParams) && !Array.isArray(assignParams)) {
      throw new Error('urlTransform(): assignParams는 객체 또는 객체 배열이어야 합니다.');
    }
    if (Array.isArray(assignParams) && !assignParams.every(item => isPlainObject(item))) {
      throw new Error('urlTransform(): assignParams 배열의 모든 요소는 객체여야 합니다.');
    }
  }

  // appendParams 검증: 객체 또는 객체 배열 허용
  if (appendParams !== undefined) {
    if (!isPlainObject(appendParams) && !Array.isArray(appendParams)) {
      throw new Error('urlTransform(): appendParams는 객체 또는 객체 배열이어야 합니다.');
    }
    if (Array.isArray(appendParams) && !appendParams.every(item => isPlainObject(item))) {
      throw new Error('urlTransform(): appendParams 배열의 모든 요소는 객체여야 합니다.');
    }
  }

  // 기존 URL의 파라미터를 파싱 (URLSearchParams 사용)
  const urlSearchParams = new URLSearchParams(originalUrl.search);
  const urlParams = {};

  // URLSearchParams를 일반 객체로 변환 (중복 키 처리)
  for (const key of urlSearchParams.keys()) {
    const values = urlSearchParams.getAll(key);
    if (values.length === 1) {
      urlParams[key] = values[0];
    } else {
      urlParams[key] = values;
    }
  }

  // 1. deleteParams 처리: 지정된 파라미터 이름으로 시작하는 모든 파라미터 삭제
  if (deleteParams) {
    deleteParams.forEach(paramPrefix => {
      Object.keys(urlParams).forEach(key => {
        if (key === paramPrefix || key.startsWith(`${paramPrefix}[`)) {
          delete urlParams[key];
        }
      });
    });
  }

  // 2. assignParams 처리: 파라미터 덮어쓰기 (뒤에서 앞으로 병합)
  if (assignParams) {
    if (Array.isArray(assignParams)) {
      // 배열인 경우 순서대로 병합 (뒤의 객체가 앞의 객체를 덮어씀)
      assignParams.forEach(paramObj => {
        Object.assign(urlParams, paramObj);
      });
    } else {
      // 객체인 경우 직접 병합
      Object.assign(urlParams, assignParams);
    }
  }

  // 3. appendParams 처리: 파라미터 추가 (기존 값이 있으면 유지)
  if (appendParams) {
    const appendEntries = Array.isArray(appendParams)
      ? appendParams.flatMap(obj => Object.entries(obj))
      : Object.entries(appendParams);

    appendEntries.forEach(([key, value]) => {
      if (!(key in urlParams)) {
        urlParams[key] = value;
      }
    });
  }

  // 변경된 파라미터로 새 URL 생성 (URLSearchParams 사용)
  const newSearchParams = new URLSearchParams();
  Object.entries(urlParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        // 배열인 경우 각 값을 개별적으로 추가
        value.forEach(val => newSearchParams.append(key, val));
      } else {
        newSearchParams.set(key, value);
      }
    }
  });

  // 원래 입력이 상대 경로였는지 확인하여 적절한 형태로 반환
  const isOriginalRelative = !url.includes('://');

  if (isOriginalRelative) {
    // 상대 경로로 입력된 경우 상대 경로로 반환
    return `${originalUrl.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}${originalUrl.hash}`;
  } else {
    // 절대 URL로 입력된 경우 절대 URL로 반환
    return `${originalUrl.origin}${originalUrl.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}${originalUrl.hash}`;
  }
}

function queryStringSplit(url) {
  if (url === undefined) {
    throw new Error("queryStringSplit url is undefined")
  }
  const queryStartIndex = url.indexOf("?")
  if (queryStartIndex === -1) {
    if (/^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//.test(url)) {
      return [url, ""]
    } else {
      return ["", url]
    }
  }
  const queryString = url.slice(queryStartIndex + 1)
  return [url.slice(0, queryStartIndex), queryString]
}

export function queryStringify(data, { encode = false, arrayFormat = "brackets", allowEmptyArrays = true } = {}) {
  return qs.stringify(data, { encode, arrayFormat, allowEmptyArrays })
}

// return obejct 
export function queryStringParse(url) {
  if (isPlainObject(url)) {
    return reflect(url)
  } else if (typeof url === "string") {
    const [baseUrl, originalQs] = queryStringSplit(url)
    return qs.parse(originalQs)
  } else {
    return {}
  }
}

/**
 * 기존 URL의 쿼리스트링과 새로운 데이터를 병합하여 새로운 URL을 반환합니다.
 * 기본적으로 data에 명시적으로 키가 있으면 그 값으로 덮어쓰고,
 * data에 undefined, null, NaN 등이 들어오면 해당 키를 쿼리스트링에서 제거합니다.
 * assignProc을 커스터마이즈하여 병합 방식을 변경할 수 있습니다.
 *
 * @param {string} url - 기존 URL (쿼리스트링 포함 가능)
 * @param {Object|string|Array<Object|string>} data - 병합할 쿼리 데이터(객체 또는 쿼리스트링)
 * @param {function} [assignProc] - (key, [a, b], [aRef, bRef]) => value
 *   병합 로직을 커스터마이즈할 수 있는 함수
 *   - key: 현재 키
 *   - [a, b]: [기존 값, 새 값]
 *   - [aRef, bRef]: [기존 데이터 객체, 새 데이터 객체]
 *   기본값: (key, [a, b], [aRef, bRef]) => (bRef.hasOwnProperty(key) && b !== undefined ? b : a)
 * @returns {string} 병합된 쿼리스트링이 포함된 URL
 *
 * @example
 * // 기존 쿼리스트링에 새 값을 덮어씌움
 * queryStringAssign('https://example.com?a=1&b=2', { b: 3, c: 4 })
 * // => 'https://example.com?a=1&b=3&c=4'
 *
 * @example
 * // data에 null, undefined, NaN을 넣으면 해당 키가 삭제됨
 * queryStringAssign('https://example.com?a=1&b=2', { b: null, c: undefined, d: NaN })
 * // => 'https://example.com?a=1'
 *
 * @example
 * // assignProc을 커스터마이즈하여 병합 방식 변경
 * queryStringAssign(
 *   'https://example.com?a=1&b=2',
 *   { b: 0, c: 4 },
 *   (key, [a, b], [aRef, bRef]) => (bRef.hasOwnProperty(key) ? b : a)
 * )
 * // => 'https://example.com?a=1&b=0&c=4'
 */
export function queryStringAssign(url, data, assignProc = (key, [a, b], [aRef, bRef]) => (bRef.hasOwnProperty(key) && b !== undefined ? b : a)) {

  const [baseUrl, originalQs] = queryStringSplit(url)
  const assignQueue = asArray(data)

  // 최초 쿼리스트링에서 시작
  let assignedQueueData = queryStringParse(originalQs)

  // 배열의 각 데이터를 순차적으로 병합
  for (const assignInput of assignQueue) {
    const incomeData = queryStringParse(assignInput)
    const originalData = { ...assignedQueueData } // 이전 결과를 원본으로 사용
    const dataRefs = [Object.freeze(originalData), Object.freeze(incomeData)]
    const allKeysSet = new Set([...Object.keys(originalData), ...Object.keys(incomeData)])

    const result = {}
    allKeysSet.forEach((key) => {
      const a = originalData[key]
      const b = incomeData[key]
      const value = assignProc(key, [a, b], dataRefs)
      if (!isNone(value)) {
        result[key] = value
      }
    })

    assignedQueueData = result
  }

  const assignedQsString = queryStringify(assignedQueueData)

  if (assignedQsString) {
    return baseUrl + "?" + assignedQsString
  } else {
    return baseUrl
  }
}

export const READABLE_FORM_INPUT_SELECTOR = "input:not([type=submit]),select,textarea,checkbox,radio"
export const WRITABLE_FORM_INPUT_SELECTOR = "input:not([type=hidden]):not([type=submit]),select,textarea,checkbox,radio"

export function filterWritableFormInputElements(element) {
  //querySelector나 matches 가 없는 다른 타입의 노드의 경우 빈 배열로 반환
  if (!(element instanceof HTMLElement)) {
    return []
  }
  // shadow가 있는 element의 경우 shadowRoot를 이용
  if (element instanceof HTMLElement) {
    element = element.shadowRoot ?? element
  }
  if (element.matches(WRITABLE_FORM_INPUT_SELECTOR)) {
    return [element]
  } else {
    return Array.from(element.querySelectorAll(WRITABLE_FORM_INPUT_SELECTOR))
  }
}

export function isDirtyElement(inputSelector) {
  const input = selectElement(inputSelector)
  if (!input) throw new Error(`isDirtyElement No input element found => ${inputSelector}`)
  return valueOfXNode(input) != input.getAttribute("value")
}

export function plainFormData(formSelectQuery) {
  const form = selectElement(formSelectQuery)
  const formData = new FormData(form)
  return Object.fromEntries(formData.entries());
}

// rails 파라메터 스타일의 키(input name)를 평범한 오브젝트 파일로 변환
export function railsParamsStyleToPlainData(plainData) {
  const result = {};

  for (const key in plainData) {
    if (plainData.hasOwnProperty(key)) {
      const value = plainData[key];
      const parts = key.split('[');
      let current = result;

      for (let i = 0; i < parts.length; i++) {
        let part = parts[i].replace(']', '');

        if (i === parts.length - 1) {
          if (part === '') {
            if (Array.isArray(current)) {
              current.push(value);
            } else {
              current = value;
            }
          } else {
            current[part] = value;
          }
        } else {
          if (part === '') {
            console.warn('빈 키가 중간에 있습니다:', key);
            break;
          }

          if (!current[part]) {
            if (parts[i + 1] && parts[i + 1].replace(']', '') === '') {
              current[part] = [];
            } else {
              current[part] = {};
            }
          }
          current = current[part];
        }
      }
    }
  }

  return result;
}

// 특정 영역에서의 input 요소를 읽어 평범한 오브젝트 파일로 변환합니다.
export function asRailsPlainDataWithContextElement(contextSelectQuery, options = {}) {
  // 옵션 처리
  let processedOptions = {};

  // 옵션이 함수인 경우 filter로 처리
  if (typeof options === 'function') {
    processedOptions = { filter: options };
  } else {
    ㅖd
    processedOptions = options;
  }

  // 기본 옵션 설정
  const {
    selectDisabled = true,
    selectReadonly = true,
    filter = () => true
  } = processedOptions;

  const contextElement = selectElement(contextSelectQuery);
  if (!contextElement) {
    return null;
  }

  if (contextElement.tagName === 'FORM') {
    // Form 요소인 경우 FormData 사용
    // FormData는 기본적으로 disabled 요소를 제외하므로 별도 처리 필요
    if (selectDisabled) {
      // disabled 요소도 포함하려면 수동으로 처리
      const allInputs = Array.from(contextElement.querySelectorAll(READABLE_FORM_INPUT_SELECTOR))
        .filter(input => input.name && (selectReadonly || !input.readOnly) && filter(input));

      const data = {};

      allInputs.forEach(input => {
        if ((input.type === 'checkbox' || input.type === 'radio')) {
          if (input.checked) {
            if (input.name.endsWith('[]')) {
              const name = input.name;
              if (!data[name]) {
                data[name] = [];
              }
              data[name].push(input.value);
            } else {
              data[input.name] = input.value;
            }
          }
        } else if (input.type === 'file') {
          if (input.files.length > 0) {
            data[input.name] = input.files;
          }
        } else {
          data[input.name] = input.value;
        }
      });

      return railsParamsStyleToPlainData(data);
    } else {
      // disabled 요소를 제외하는 경우 기본 FormData 사용
      const data = plainFormData(contextElement);
      return railsParamsStyleToPlainData(data);
    }
  } else {
    // Form 요소가 아닌 경우
    const data = Array.from(contextElement.querySelectorAll(READABLE_FORM_INPUT_SELECTOR))
      .filter(input => {
        return input.name &&
          (selectDisabled || !input.disabled) &&
          (selectReadonly || !input.readOnly) &&
          filter(input);
      })
      .reduce((previous, input) => {
        if ((input.type === 'checkbox' || input.type === 'radio')) {
          if (input.checked) {
            if (input.name.endsWith('[]')) {
              const name = input.name;
              if (!previous[name]) {
                previous[name] = [];
              }
              previous[name].push(input.value);
            } else {
              previous[input.name] = input.value;
            }
          }
        } else if (input.type === 'file') {
          if (input.files.length > 0) {
            previous[input.name] = input.files;
          }
        } else {
          previous[input.name] = input.value;
        }

        return previous;
      }, {});

    return railsParamsStyleToPlainData(data);
  }
}

export function formHelper(formSelectQuery, formEventCallbacks) {
  let primaryFormId = null
  let primaryForm = null
  let primaryFormHandleSubmit = null
  let primaryFormHandleSubmitStart = null
  let primaryFormHandleSubmitEnd = null
  let emitCallbacks = null

  const base = {
    checkpoints: [],
    // formValid: false,
    requestValidate() {
      // 모든 체크포인트의 validity를 검사
      let allValid = true;
      base.checkpoints.forEach(point => {
        if (!point.target.checkValidity()) {
          allValid = false;
          point.target.reportValidity?.(); // 브라우저 기본 invalid UI 표시
          point.target.dispatchEvent(new CustomEvent("formhelper:invalid", { detail: { id: point.id } }));
        }
      });
      return allValid;
    },
    submit() {
      primaryForm.submit()
    },
    get formChanged() {
      const changedValue = base.checkpoints.map(point => {
        return isDirtyElement(point.target)
      });
      return changedValue.includes(true)
    },
    get formValid() {
      return base.checkpoints.every(point => point.target.checkValidity());
    },
    get formValidity() {
      return base.checkpoints.map(point => {
        const value = valueOfXNode(point.target)
        const originalValue = point.target.getAttribute("value")
        return {
          id: point.id,
          valid: point.target.checkValidity(),
          dirty: value != originalValue,
          value,
          originalValue,
        }
      });
    },
    get vs() {
      const valids = []
      const invalids = []
      base.formValidity.forEach((validity) => {
        if (validity.valid) {
          valids.push(validity)
        } else {
          invalids.push(validity)
        }
      })
      return {
        primaryFormId,
        valids,
        invalids,
      }
    },
    reset() {
      primaryForm.reset()
    },
    update(input) {
      // dispatchElement(input, "customvalidation:update")
      dispatchElement(input, "formhelper:update")
      return input
    },
    commit() {
      base.checkpoints.forEach(point => {
        if (point.target.getAttribute("value") != point.target.value) {
          point.target.setAttribute("value", point.target.value)
          point.target.dispatchEvent(new CustomEvent("formhelper:update", { detail: { id: point.id, value: point.target.value } }))
        }
      })
    },
    destroy() {
      primaryForm.removeAttribute("data-fh-root")
      primaryForm.removeEventListener("submit", primaryFormHandleSubmit)
      // turbo 전용 이벤트
      primaryForm.removeEventListener("turbo:submit-start", primaryFormHandleSubmitStart)
      primaryForm.removeEventListener("turbo:submit-end", primaryFormHandleSubmitEnd)
      base.checkpoints.forEach(point => {
        point.destroyPoint()
      })
    }
  }


  async function setupPrimaryVariants(formSelectQuery) {
    // form element 를 찾을 수 없는 경우 에러
    const selectNewForm = selectElement(formSelectQuery)
    if (!selectNewForm) {
      throw new Error(`formHelper No form element found => ${formSelectQuery}`)
    }

    // 매개변수의 element는 form element 여야 함
    if (selectNewForm.tagName !== "FORM") {
      throw new Error(`formHelper form element must be <form> => ${formSelectQuery}`)
    }

    // 이미 form validator 로 초기화된 상황이면 에러
    if (selectNewForm.getAttribute("data-fh-root")) {
      throw new Error(`formHelper form element already have data-fh-root attribute => ${formSelectQuery}`)
    }

    // primary form 초기화
    primaryFormId = generateUUID("xxxxxx")
    selectNewForm.setAttribute("data-fh-root", primaryFormId)
    primaryForm = selectNewForm
    primaryFormHandleSubmit = function (event) {
      emitCallbacks?.submit?.(event)
    }
    primaryFormHandleSubmitStart = function (event) {
      emitCallbacks?.submitStart?.(event)
    }
    primaryFormHandleSubmitEnd = function (event) {
      emitCallbacks?.submitEnd?.(event)
    }
    primaryForm.addEventListener("submit", primaryFormHandleSubmit)
    primaryForm.addEventListener("turbo:submit-start", primaryFormHandleSubmitStart)
    primaryForm.addEventListener("turbo:submit-end", primaryFormHandleSubmitEnd)
    return {
      primaryForm,
      primaryFormId,
    }
  }

  // 이벤트 콜백 생성
  function setupEmitCallbacks(formEventCallbacks) {
    if (isPlainObject(formEventCallbacks)) {
      emitCallbacks = formEventCallbacks
      return { emitCallbacks }
    }
    if (typeof formEventCallbacks === "function") {
      const callbackValue = formEventCallbacks({ "data-fh-root": primaryFormId })
      if (isPlainObject(callbackValue)) {
        emitCallbacks = callbackValue
        return { emitCallbacks }
      }
    }
    throw new Error(`formHelper Invalid formEventCallbacks => ${formEventCallbacks}`)
  }


  function setupBaseCheckpoints({ primaryForm, primaryFormId, emitCallbacks, emitFinishUpdateInputs }) {
    let currentRetainCount = 0
    const initialWritableInputs = getTargetInputs(primaryForm)

    // 체크포인트 아이템 생성 함수
    function pushCheckpoint(target) {
      // point 의 고유 아이디 생성
      const currentTargetId = `${primaryFormId}_${currentRetainCount}`
      currentRetainCount++

      // point handler
      const handlers = [
        {
          name: "formhelper:update",
          handler: (event) => {
            emitCallbacks?.update?.(event)
            if (emitCallbacks?.dirty) {
              if (isDirtyElement(point.target)) {
                emitCallbacks?.dirty?.(new CustomEvent("dirty", { detail: { dirty: true, value: point.target.value } }))
              } else {
                emitCallbacks?.dirty?.(new CustomEvent("dirty", { detail: { dirty: false, value: point.target.value } }))
              }
            }
            dispatchElement(target, "formhelper:updated", {})
            emitFinishUpdateInputs()
          }
        },
        // x-custom-validation 이 없으면 직접 이벤트를 감지함
        !target.getAttribute("x-custom-validations") || !target.getAttribute("data-custom-validations") ? {
          name: "input",
          handler: () => {
            dispatchElement(target, "formhelper:update")
          }
        } : null,
        // x-custom-validation 이 없으면 직접 이벤트를 감지함
        !target.getAttribute("x-custom-validations") || !target.getAttribute("data-custom-validations") ? {
          name: "touch",
          handler: () => {
            dispatchElement(target, "formhelper:update")
          }
        } : null,
        {
          name: "customvalidation:updated",
          handler: () => {
            setTimeout(() => {
              dispatchElement(target, "formhelper:update")
            }, 0)
          }
        },
        // {
        //   name: "blur",
        //   handler: ()=>{
        //     dispatchElement(target, "formhelper:update")
        //   }
        // },
      ].filter(Boolean)

      handlers.forEach(handler => { target.addEventListener(handler.name, handler.handler) })
      target.setAttribute("data-fh-target", currentTargetId)
      const point = Alpine.reactive({
        id: currentTargetId,
        target,
        handlers,
        destroyPoint() {
          const removeIndex = base.checkpoints.findIndex((point) => { return point.id === currentTargetId })
          handlers.forEach(handler => { target.removeEventListener(handler.name, handler.handler) })
          target.removeAttribute("data-fh-target")
          base.checkpoints.splice(removeIndex, 1)
          emitCallbacks?.exit?.(point)
        }
      })
      base.checkpoints.push(point)
      setTimeout(() => {
        emitCallbacks?.enter?.(point)
      })
      return point
    }

    // 초기 체크포인트 생성
    initialWritableInputs.forEach(pushCheckpoint)

    // document dom의 변화를 감지하여 체크포인트 내용 업데이트
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(node => {
          const newInputElements = filterWritableFormInputElements(node)
          setTimeout(() => {
            const currentWritalbeInputs = getTargetInputs(primaryForm)
            const newTargetInputs = currentWritalbeInputs.filter((element) => {
              return newInputElements.includes(element)
            })
            if (newTargetInputs.length) {
              newTargetInputs.forEach((element) => {
                pushCheckpoint(element)
              })
              emitFinishUpdateInputs()
            }
          }, 0)
        })
        mutation.removedNodes.forEach(node => {
          const removeInputElements = filterWritableFormInputElements(node).filter((element) => {
            return (element.getAttribute('data-fh-target') || "").startsWith(primaryFormId)
          })
          const destroyPoints = base.checkpoints.map((checkpoint) => {
            if (removeInputElements.includes(checkpoint.target)) {
              return checkpoint
            }
            return null
          }).filter(Boolean)
          if (destroyPoints.length) {
            destroyPoints.forEach((checkpoint) => {
              checkpoint.destroyPoint()
            })
            emitFinishUpdateInputs()
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // 페이지 이동 시 체크포인트 제거
    function handleDestroy() {
      observer.disconnect()
      document.removeEventListener("turbo:before-visit", handleDestroy)
    }

    document.addEventListener("turbo:before-visit", handleDestroy)
    return { base }
  }

  async function install() {
    setupPrimaryVariants(formSelectQuery)
      .then((assignedProps) => {
        const emitCallbackResult = setupEmitCallbacks(formEventCallbacks, assignedProps)
        return { ...assignedProps, ...emitCallbackResult }
      }).then((assignedProps) => {
        function emitFinishUpdateInputs() {
          assignedProps.emitCallbacks?.valid?.(base.formValid)
          assignedProps.emitCallbacks?.validity?.(base.formValidity)
          assignedProps.emitCallbacks?.vs?.(base.vs)
        }
        const checkpointsResult = setupBaseCheckpoints({ emitFinishUpdateInputs, ...assignedProps })
        return { ...assignedProps, ...checkpointsResult, emitFinishUpdateInputs }
      }).then(({ emitFinishUpdateInputs }) => {
        emitFinishUpdateInputs()
      }).catch((error) => {
        console.error("Form helepr install not success", error)
        return Promise.reject(error)
      })
  }

  base.promise = install()
  return base
}

function buildTargetInputsSelector(form) {
  // form 엘리먼트
  if (form.matches("form")) {
    const queryBuilder = []

    // form 내부 검색
    // data-fh-root 지원시
    const primaryFormId = form.getAttribute("data-fh-root")
    if (form.id) {
      const formChildrenSelector = WRITABLE_FORM_INPUT_SELECTOR.split(",").map(selector => {
        return `[data-fh-root="${primaryFormId}"] ${selector}`
      }).join(",")
      queryBuilder.push(formChildrenSelector)
    } else {
      // 단서가 없음으로 unstable 하지만 nth-child로 찾습니다.
      const allForms = Array.from(document.querySelectorAll("form"))
      const formIndex = allForms.findIndex(form => form === this)
      const formUnstableChildrenSelector = WRITABLE_FORM_INPUT_SELECTOR.split(",").map(selector => {
        return `form:nth-child(${formIndex + 1}) ${selector}`
      }).join(",")
      queryBuilder.push(formUnstableChildrenSelector)
    }

    // form 외부 검색
    if (form.id) {
      const formIdSelector = WRITABLE_FORM_INPUT_SELECTOR.split(",").map(selector => {
        return `${selector}[form="${form.id}"]`
      }).join(",")
      queryBuilder.push(formIdSelector)
    }

    const currentTargetSelector = queryBuilder.join(",")

    return {
      queryRoot: document.body,
      selector: currentTargetSelector,
      method: "querySelectorAll"
    }
  } else {
    // 일반 엘리먼트가 들어오는 경우
    return {
      queryRoot: form,
      selector: WRITABLE_FORM_INPUT_SELECTOR,
      method: "querySelectorAll"
    }
  }
}

function getTargetInputs(form) {
  const { queryRoot, method, selector } = buildTargetInputsSelector(form)
  return Array.from(queryRoot[method](selector))
}


